import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { I18NextPipe } from "angular-i18next";
import { Subject } from "rxjs";
import { ManualRowSortComponent } from "src/app/dashboards/components/manual-row-sort/manual-row-sort.component";
import { SortComponent } from "src/app/dashboards/components/sort-component/sort.component";
import { AppEventType, tableColorScheme } from "src/app/enums";
import { Table, TableThemeStyles } from "src/app/models";
import { EventQueueService } from "src/app/services/services/EventQueueService";
import { pad, PercentageFields, TimeObject } from "../../widget-helpers/helpers";

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    standalone: false
})

export class TableComponent implements OnInit {
    @ViewChild(MatSort) sort: MatSort;
    @Input() isInWizard: boolean;
    @Input() data: any;
    @Input() alarmItems: any;
    @Input() propsWithIds: any;
    @Input() gridItem: any;
    @Input() type: string;
    @Input() colorSchemeId: number;
    @Input() notifier: Subject<any>;

    public table: Table = {
        data: [],
        displayedColumns: [],
        styles: {
            header: "",
            oddRow: "",
            evenRow: "",
            alarmRow: "",
            headerCol: "",
            rowCol: "",
            isDarkTheme: false
        }
    };

    public alarmProps: any = [];
    public alarmTitle: string = "";

    tooltipOptions = {
        "contentType": "template",
        "tooltipClass": "alarmClass",
        "placement": "bottom",
        "hideDelay": 100,
        "offset": 10,
        "shadow": false
    }

    alarmQueue: string;
    alarmName: string;
    basicQueue: string;
    thresholdTypeName: string;
    alarmTime: string;
    rowFontSize: number;
    headerFontSize: number;
    rowColor: string;
    headerColor: string;

    get secondaryLevelSortingColumn(): string {
      return this.gridItem.setup[SortComponent.secondLevelSetupColumnPropertyName];
    }

  get secondaryLevelSortingDirection(): string {
    return this.gridItem.setup[SortComponent.secondLevelSetupDirectionPropertyName] == null ? 'asc' : this.gridItem.setup[SortComponent.secondLevelSetupDirectionPropertyName] == 1 ? 'desc' : 'asc';
    }

    get isSecondLevelSortingEnabled(): boolean {
      return !!this.secondaryLevelSortingColumn;
    }

    get isManualRowOrderEnabled() {
      return ManualRowSortComponent.getIsManualRowSortEnabled(this.gridItem);
    }

    private clearDashboardRequested: boolean = false;

    private _sortingValueComparision(valueA: any, valueB: any): number {
      // If there are data in the column that can be converted to a number,
      // it must be ensured that the rest of the data
      // is of the same type so as not to order incorrectly.
      const valueAType = typeof valueA;
      const valueBType = typeof valueB;
      if (valueAType !== valueBType) {
        if (valueAType === 'number') {
          valueA += '';
        }
        if (valueBType === 'number') {
          valueB += '';
        }
      }

      // If both valueA and valueB exist (truthy), then compare the two. Otherwise, check if
      // one value exists while the other doesn't. In this case, existing value should come last.
      // This avoids inconsistent results when comparing values to undefined/null.
      // If neither value exists, return 0 (equal).
      let comparatorResult = 0;
      if (valueA != null && valueB != null) {
        // Check if one value is greater than the other; if equal, comparatorResult should remain 0.
        if (valueA > valueB) {
          comparatorResult = 1;
        }
        else if (valueA < valueB) {
          comparatorResult = -1;
        }
      }
      else if (valueA == null && valueB == null) {
        comparatorResult = 0;
      }
      else if (valueA != null) {
        comparatorResult = 1;
      }
      else if (valueB != null) {
        comparatorResult = -1;
      }

      return comparatorResult;
    }

    //// Initial sortData function as it was in the webpack:///node_modules/@angular/material/__ivy_ngcc__/fesm2015/table.js
    //  (data, sort) => {
    //  const active = sort.active;
    //  const direction = sort.direction;
    //  if (!active || direction == '') {
    //      return data;
    //  }
    //  return data.sort((a, b) => {
    //      let valueA = this.sortingDataAccessor(a, active);
    //      let valueB = this.sortingDataAccessor(b, active);
    //      // If there are data in the column that can be converted to a number,
    //      // it must be ensured that the rest of the data
    //      // is of the same type so as not to order incorrectly.
    //      const valueAType = typeof valueA;
    //      const valueBType = typeof valueB;
    //      if (valueAType !== valueBType) {
    //          if (valueAType === 'number') {
    //              valueA += '';
    //          }
    //          if (valueBType === 'number') {
    //              valueB += '';
    //          }
    //      }
    //      // If both valueA and valueB exist (truthy), then compare the two. Otherwise, check if
    //      // one value exists while the other doesn't. In this case, existing value should come last.
    //      // This avoids inconsistent results when comparing values to undefined/null.
    //      // If neither value exists, return 0 (equal).
    //      let comparatorResult = 0;
    //      if (valueA != null && valueB != null) {
    //          // Check if one value is greater than the other; if equal, comparatorResult should remain 0.
    //          if (valueA > valueB) {
    //              comparatorResult = 1;
    //          }
    //          else if (valueA < valueB) {
    //              comparatorResult = -1;
    //          }
    //      }
    //      else if (valueA != null) {
    //          comparatorResult = 1;
    //      }
    //      else if (valueB != null) {
    //          comparatorResult = -1;
    //      }
    //      return comparatorResult * (direction == 'asc' ? 1 : -1);
    //  });
    //};
    get sortDataFn(): (data: any[], sort: MatSort) => any[] {
      let setup = this.gridItem.setup;
      let self = this;

      return function (data: any[], sort: MatSort) {
        const active = sort.active;
        const direction1 = sort.direction;
        if ((!active || direction1 == '') && !self.isManualRowOrderEnabled) {
          return data;
        }

        let direction2: string = self.secondaryLevelSortingDirection;

        return data.sort((rowA, rowB) => {
          let result: number;
          let columnName = self.isManualRowOrderEnabled ? "Id" : active;

          let valueA = this.sortingDataAccessor(rowA, columnName);
          let valueB = this.sortingDataAccessor(rowB, columnName);

          if (self.isManualRowOrderEnabled) {
            let orderList: number[] = setup[ManualRowSortComponent.setupPropertyName];

            let indexA = orderList.indexOf(valueA);
            let indexB = orderList.indexOf(valueB);

            return indexA - indexB;
          } else {
            let comparatorResult = self._sortingValueComparision(valueA, valueB);
            result = comparatorResult * (direction1 == 'asc' ? 1 : -1);

            // second level of sorting
            if (result === 0 && self.isSecondLevelSortingEnabled) {
              let valueA2 = this.sortingDataAccessor(rowA, setup[SortComponent.secondLevelSetupColumnPropertyName]);
              let valueB2 = this.sortingDataAccessor(rowB, setup[SortComponent.secondLevelSetupColumnPropertyName]);

              result = self._sortingValueComparision(valueA2, valueB2);
              result = result * (direction2 == 'asc' ? 1 : -1);
            }
          }

          return result;
        });
      };
    }

    constructor(private i18nextPipe: I18NextPipe, private eventQueue: EventQueueService) { }

    ngOnInit(): void {
        this.notifier.subscribe(value => {
            setTimeout(() => {
                this.buildTable();
            }, 0)
        });

        this.eventQueue.on(AppEventType.DashboardsCancelEvent).subscribe(event => {
            this.clearDashboardRequested = true;
        });
    }

    buildTable(): void {
        const type = this.type;
        const gridItem = this.gridItem;
        const data = this.data;
        const queueAlarmItems = this.alarmItems;
        const sort = this.sort;
        const theme = gridItem.colorScheme ? tableColorScheme[gridItem.colorScheme].toLowerCase() : "default";
        let processedData = [];

        this.applySorting(data);

        if (data?.length > 0) {
            for (let i = 0; i < data.length; i++) {
                let obj = Object.assign({}, data[i]);
                obj = ({ ...obj, __originalRow: ({ ...data[i] }) })
                processedData.push(obj);
            }
        }

        if (processedData?.length > 0) {
            for (let i = 0; i < processedData.length; i++) {

                if (processedData[i]["AlarmFlag"] === 1) {
                    processedData[i]["hasAlarm"] = true;

                    let queueId: Number = processedData[i]["Id"];
                    if (queueAlarmItems.hasOwnProperty(queueId)) {
                        let queueAlarms = queueAlarmItems[queueId.toString()];
                        let alarmProps = [];
                        for (let i = 0; i < queueAlarms.length; i++) {
                            let alarmPropsObj = {
                                "basicQueue": queueAlarms[i]["basicQueue"],
                                "alarmName": queueAlarms[i]["alarmName"],
                                "thresholdTypeName": this.i18nextPipe.transform("dashboard:" + queueAlarms[i]["thresholdTypeName"]),
                                "alarmTime": queueAlarms[i]["alarmTime"],
                                "class": queueAlarms.length > 1 ? "popoverContentInline" : "popoverContent"
                            }
                            alarmProps.push(alarmPropsObj);
                        };
                        processedData[i]["alarmProps"] = alarmProps;
                    }
                } else {
                    processedData[i]["hasAlarm"] = false;
                }

                if (processedData[i].hasOwnProperty("StatusDetails")) {
                  let addPlus = false;
                  if(processedData[i]["StatusDetails"] && processedData[i]["StatusDetails"].toString().slice(-2)== " +") {
                    addPlus = true;
                    processedData[i]["StatusDetails"] = processedData[i]["StatusDetails"].slice(0, -2);
                  }
                    processedData[i]["StatusDetails"] =this.i18nextPipe.transform("dashboard:" + processedData[i]["StatusDetails"]);
                    if (addPlus) {
                      processedData[i]["StatusDetails"] += " +";
                    }
                }

                let keys = Object.keys(processedData[i]);
                keys.filter(function (value, index, arr) {
                    if (TimeObject[type].hasOwnProperty(value)) {
                        let timeValue = processedData[i][value];
                        let convertedTimeValue = TimeObject[type][value](timeValue);
                        processedData[i][value] = convertedTimeValue;
                    }

                    if (PercentageFields.indexOf(value) > -1) {
                        processedData[i][value] = processedData[i][value] + "%";
                    }
                });
            }

            const styles: TableThemeStyles = {
                header: theme + "Header",
                oddRow: theme + "OddRow",
                evenRow: theme + "EvenRow",
                alarmRow: "alarmRow",
                headerCol: theme + "HeaderCol",
                rowCol: theme + "RowCol",
                isDarkTheme: theme !== "default" && theme !== "ash"
            };

            if (this.clearDashboardRequested) {
                this.clearDashboardRequested = false;
                this.sort.active = null;
            }

            let sortedData: MatTableDataSource<any> = new MatTableDataSource([...processedData]);
            sortedData.sortingDataAccessor = (row: any, columnName: string): string | number => {
              let columnValue = columnName == "Name" ? row.Name : row.__originalRow[columnName];

              if (!isNaN(parseFloat(columnValue)) && isFinite(columnValue)) {
                // if manual row order is enabled we need number values, not strings, so later we can search for them in the respective list (of numbers)
                return this.isManualRowOrderEnabled ? columnValue : pad(columnValue, 20);
              } else {
                  return columnValue?.toLowerCase();
              }
            }

            sortedData.sortData = this.sortDataFn;
            sortedData.sort = sort;

            let sortedColumns = this.sortColumns(gridItem.setup);
            this.setFontSizes();
            this.setColors();

            sortedColumns.unshift("Name");

            const table: Table = {
                data: sortedData,
                displayedColumns: sortedColumns,
                styles: styles
            }

            this.table = table;
        }
    }

    getColumnName(name: string) {
      let columndId = this.propsWithIds.find(p => p.prop === name)?.id;
      let nameRecord = this.gridItem.setup.preferences?.names?.find(n => n.id === columndId);
      return nameRecord ? nameRecord.name : this.i18nextPipe.transform('dashboard:' + name);
    }

    setDisplayColumns(data: any[]): string[] {
        if (data.length > 0) {
            let dataKeys = Object.keys(data[0]);
            return dataKeys.filter(function (value, index, arr) {
                return value.toLowerCase() !== "id" && value.toLowerCase() !== "isodd" && value.toLocaleLowerCase() !== "alarmflag";
            });
        }
    }

    applySorting(data) {
        setTimeout(() => {
          if (data?.length > 0 && (this.gridItem.setup[SortComponent.firstLevelSetupColumnPropertyName] && this.sort.active == null || this.isInWizard)) {
            this.sort.active = "";
            let id = this.gridItem.setup[SortComponent.firstLevelSetupColumnPropertyName];
            let start: 'asc' | 'desc' = this.gridItem.setup[SortComponent.firstLevelSetupDirectionPropertyName] == 1 ? 'desc' : 'asc';

            this.sort.sort({ id, start, disableClear: false });
            }

            if (this.isInWizard) {
                this.sort.sortables.forEach((value, key) => { this.sort.disabled = true; })
            }
        }, 0);
    }

    showAlarm(element) {
        this.alarmTitle = "";
        this.alarmProps = [];
        this.alarmProps = [];
        let alarmProps = [];
        for (let i = 0; i < element.alarmProps.length; i++) {
            let alarmPropsObj = {
                "alarmName": element.alarmProps[i]["alarmName"],
                "basicQueue": element.alarmProps[i]["basicQueue"],
                "thresholdTypeName": element.alarmProps[i]["thresholdTypeName"],
                "alarmTime": element.alarmProps[i]["alarmTime"],
                "class": element.alarmProps.length > 1 ? "popoverContentInline" : "popoverContent"
            };

            alarmProps.push(alarmPropsObj);
        }

        this.alarmTitle = element.Name;
        this.alarmProps = [...alarmProps];
    }

    sortColumns(setup): string[] {
        let propIds = [];
        let sortedColumns = [];
        let propsWithIds = this.propsWithIds;
        setup.agentPropertiesIds ? propIds = setup.agentPropertiesIds : propIds = setup.queuePropertiesIds;
        for (let i = 0; i < propIds?.length; i++) {
            let column = propsWithIds.filter(p => p.id === propIds[i])[0];
            if (column) {
                sortedColumns.push(column.prop);
            }
        }

        return sortedColumns;
    }

    setFontSizes() {
        let fontSize = 14;
        this.headerFontSize = fontSize;
        this.rowFontSize = fontSize;
        const preferences = this.gridItem.setup.preferences;
        if (preferences) {
            const fonts = preferences.fonts;
            if (fonts?.length > 0) {
                if (fonts.some(f => f.id === 6)) {
                    let header = fonts.filter(f => f.id === 6)[0];
                    this.headerFontSize = header.fontSize;
                }

                if (fonts.some(f => f.id === 7)) {
                    let row = fonts.filter(f => f.id === 7)[0];
                    this.rowFontSize = row.fontSize;
                }
            }
        }
    }

    setColors() {
        let color = "";
        this.headerColor = color;
        this.rowColor = color;
        const preferences = this.gridItem.setup.preferences;
        if (preferences) {
            const colors = preferences.colors;
            if (colors?.length > 0) {
                if (colors.some(f => f.id === 6)) {
                    let header = colors.filter(f => f.id === 6)[0];
                    this.headerColor = header.color;
                }

                if (colors.some(f => f.id === 7)) {
                    let row = colors.filter(f => f.id === 7)[0];
                    this.rowColor = row.color;
                }
            }
        }
    }

    isOddRow(row: any, index: number) {
        row.isOdd = index % 2 === 1;
        return row.isOdd;
    }

    getRowClass(row: any, i: number) {
      let class_ = this.isOddRow(row, i) && !row.hasAlarm ?
                    this.table.styles.oddRow :
        !this.isOddRow(row, i) && !row.hasAlarm ? this.table.styles.evenRow : this.table.styles.alarmRow;

      return class_;
    }

    isSortArrowShown(columnName: string, direction: string) {
      return this.sort.active == columnName && this.sort.direction == direction || this.secondaryLevelSortingColumn == columnName && this.secondaryLevelSortingDirection == direction;
    }

    // on header click, the second level of sorting is removed from the fron-end (not from configuration saved in DB)
    headerClick() {
        // no sorting triggered by click is allowed in the wizard
        if (this.isInWizard) {
          return;
        }

        this.gridItem.setup[SortComponent.secondLevelSetupColumnPropertyName] = null;
        this.gridItem.setup[SortComponent.secondLevelSetupDirectionPropertyName] = null;
        this.gridItem.setup[ManualRowSortComponent.setupPropertyName] = null;
    }
}
